// Surplus de matériel (décision du 22/10/2026) : encadré ligne par ligne, case « Intégrer le surplus au devis », réglage du taux.
// Le serveur calcule tout (shared/surplus.ts) ; l'interface n'affiche que ce qu'il renvoie, jamais un taux deviné.
import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, messageErreur } from '../../lib/api.ts';
import { nombre } from '../../lib/format.ts';
import { Alerte, Badge, Bouton, Champ, ChampTexte, Dialogue, Panneau, useNotifier } from '../../components/ui.tsx';
import '../../styles/surplus.css';
import { controleTaux, horsRecommandation, TAUX_RECOMMANDE, type StatutSurplus } from '@shared/surplus.ts';

const q = (v: string | null | undefined) => (v == null ? '—' : nombre(v, 4));
const pct = (v: string | null) => (v == null ? '' : `${nombre(v, 2)} %`);
const TON: Record<StatutSurplus, 'accent' | 'neutre' | 'ambre' | 'info' | 'succes'> = {
  surplus: 'accent', sans_surplus: 'neutre', a_decider: 'ambre', deja_majoree: 'info', deja_au_devis: 'succes',
};

/** Ligne de l'encadré, forme commune au devis et au bon de commande. */
export interface LigneSurplusVue {
  cle: string; label: string; reference: string | null; unit: string; statut: StatutSurplus; famille_libelle: string | null;
  taux: string | null; source_taux: 'saisi' | 'article' | 'famille' | null; motif: string | null; taux_modifiable: boolean;
  besoin_net: string; surplus: string; quantite: string; arrondi: string | null; conditionnement: string | null; nombre: string | null; detail: string;
  /** Texte de la dernière colonne (ex. « vendu au devis : 102 m »). */
  note?: ReactNode;
}

/** Libellés courts des badges (le détail complet est sous les chiffres). */
const BADGE: Record<StatutSurplus, string> = {
  surplus: 'Surplus', sans_surplus: 'Pas de surplus', a_decider: 'Surplus à décider', deja_majoree: 'Déjà majorée au métré', deja_au_devis: 'Déjà compris au devis',
};
const SOURCE_TAUX = { saisi: 'choisi ici', article: 'réglé sur l’article', famille: 'taux de la famille' } as const;

export function EncadreSurplus({ lignes, libelleQuantite, regler, entete }: { lignes: LigneSurplusVue[]; libelleQuantite: string;
  regler?: (l: LigneSurplusVue) => void; entete?: ReactNode }) {
  return (
    <section className="surplus-encadre" aria-label="Surplus de matériel">
      {entete}
      <ul className="surplus-liste">
        {lignes.map((l) => (
          <li key={l.cle} className="surplus-carte">
            <div className="surplus-carte-titre">
              <span className="surplus-libelle">{l.label}{l.reference && <span className="mono tres-petit discret"> {l.reference}</span>}</span>
              <Badge ton={TON[l.statut]} icone={false}>{l.statut === 'surplus' ? `Surplus ${pct(l.taux)}` : BADGE[l.statut]}</Badge>
            </div>
            <dl className="surplus-chiffres">
              <div><dt>Besoin net</dt><dd className="chiffre">{q(l.besoin_net)} {l.unit}</dd></div>
              <div><dt>Surplus</dt><dd className="chiffre">{l.statut === 'surplus' ? `${q(l.surplus)} ${l.unit}` : '—'}</dd></div>
              <div><dt>Arrondi</dt><dd className="chiffre">{l.arrondi == null ? '—' : `${q(l.arrondi)} ${l.unit}`}</dd></div>
              <div><dt>{libelleQuantite}</dt><dd className="chiffre"><strong>{q(l.quantite)} {l.unit}</strong></dd></div>
            </dl>
            <p className="tres-petit secondaire surplus-detail">
              {l.famille_libelle ? `Famille : ${l.famille_libelle}. ` : ''}
              {l.statut === 'surplus' && l.source_taux ? `Taux ${SOURCE_TAUX[l.source_taux]}${horsRecommandation(l.taux) ? ' — hors de la plage recommandée (15 à 20 %)' : ''}. ` : ''}
              {l.motif ? `Raison : ${l.motif}. ` : ''}
              {l.conditionnement ? `Conditionnement : ${l.nombre ?? '?'} × ${l.conditionnement}. ` : 'Conditionnement à renseigner : arrondi à l’unité supérieure. '}
              {l.statut !== 'surplus' ? l.detail : ''}
            </p>
            {(l.note || (regler && l.taux_modifiable)) && <div className="rangee surplus-pied">
              {l.note && <span className="petit">{l.note}</span>}
              {regler && l.taux_modifiable && <Bouton compact onClick={() => regler(l)}>Ajuster le taux</Bouton>}
            </div>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Réglage du taux d'une ligne : 0 à 30 %, plage recommandée 15 à 20 %, raison écrite hors plage. */
export function DialogueTauxSurplus({ ligne, fermer, enregistrer }: { ligne: LigneSurplusVue | null; fermer: () => void;
  enregistrer: (taux: string | null, motif: string | null) => Promise<void> }) {
  const [taux, setTaux] = useState(''); const [motif, setMotif] = useState(''); const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false); const [ouvertPour, setOuvertPour] = useState<string | null>(null);
  if (ligne && ouvertPour !== ligne.cle) { setOuvertPour(ligne.cle); setTaux(ligne.source_taux === 'saisi' && ligne.taux ? ligne.taux.replace('.', ',') : ''); setMotif(ligne.motif ?? ''); setErreur(null); }
  if (!ligne && ouvertPour !== null) setOuvertPour(null);
  const hors = taux.trim() !== '' && !controleTaux(taux, 'xxxxx') && horsRecommandation(taux.replace(',', '.'));
  const valider = async (t: string | null) => {
    if (t !== null) { const e = controleTaux(t, motif); if (e) { setErreur(e); return; } }
    setEnvoi(true); setErreur(null);
    try { await enregistrer(t === null ? null : t.replace(',', '.'), t === null ? null : motif.trim() || null); fermer(); } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre={ligne ? `Taux de surplus — ${ligne.reference ?? ligne.label}` : 'Taux de surplus'} ouvert={!!ligne} fermer={fermer} pied={<>
      <Bouton onClick={fermer}>Annuler</Bouton>
      {ligne?.source_taux === 'saisi' && <Bouton chargement={envoi} onClick={() => valider(null)}>Revenir au taux de l’article</Bouton>}
      <Bouton variante="principal" chargement={envoi} disabled={!taux.trim()} onClick={() => valider(taux)}>Appliquer</Bouton>
    </>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <p className="petit">Plage recommandée : {TAUX_RECOMMANDE.min} à {TAUX_RECOMMANDE.max} %. Valeur possible : 0 à 30 %. En dehors de la plage recommandée, écrivez la raison : elle est enregistrée.</p>
        <Champ libelle="Taux de surplus (%)" inputMode="decimal" value={taux} placeholder={ligne?.taux ? ligne.taux.replace('.', ',') : ''} onChange={(e) => setTaux(e.target.value)} />
        {hors && <ChampTexte libelle="Raison (obligatoire hors 15 à 20 %)" maxLength={500} rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} />}
      </div>
    </Dialogue>
  );
}

// ─── Devis ───────────────────────────────────────────────────────────────────────────────────────
interface LigneEncadreDevis {
  ligne_id: string; label: string; reference: string | null; unit: string; statut: StatutSurplus; famille_libelle: string | null; taux: string | null;
  source_taux: LigneSurplusVue['source_taux']; motif: string | null; taux_modifiable: boolean; besoin_net: string; surplus: string;
  conditionnement: { quantite: string; libelle: string; fournisseur: string | null } | null; nombre_conditionnements: string | null;
  quantite_arrondie: string; arrondi: string; quantite_si_integre: string; quantite_devis: string; integree: boolean; a_jour: boolean; detail: string; ecart_devis: string | null;
}
interface VueSurplusDevis { integre: boolean; fige: boolean; modifiable: boolean; a_jour: boolean; lignes: LigneEncadreDevis[] }

export function PanneauSurplusDevis({ rid, apresEcriture }: { rid: string; apresEcriture: () => Promise<void> }) {
  const qc = useQueryClient(); const notifier = useNotifier();
  const r = useQuery({ queryKey: ['surplus-devis', rid], queryFn: () => api.get<VueSurplusDevis>(`/api/devis/revisions/${rid}/surplus`) });
  const [envoi, setEnvoi] = useState(false); const [regle, setRegle] = useState<LigneSurplusVue | null>(null);
  if (r.isError) return <Alerte ton="danger">Surplus : {messageErreur(r.error)}</Alerte>;
  const v = r.data;
  if (!v || !v.lignes.length) return null;
  const envoyer = async (corps: Record<string, unknown>) => {
    setEnvoi(true);
    try {
      const x = await api.post<VueSurplusDevis>(`/api/devis/revisions/${rid}/surplus`, corps);
      qc.setQueryData(['surplus-devis', rid], { ...v, ...x }); await apresEcriture();
      notifier(x.integre ? 'Surplus intégré au devis. Choix enregistré dans le journal.' : 'Surplus retiré du devis : il reste à la charge de l’entreprise. Choix enregistré dans le journal.');
    } catch (e) { notifier(messageErreur(e), true); throw e; } finally { setEnvoi(false); }
  };
  const vues: LigneSurplusVue[] = v.lignes.map((l) => ({ cle: l.ligne_id, label: l.label, reference: l.reference, unit: l.unit, statut: l.statut,
    famille_libelle: l.famille_libelle, taux: l.taux, source_taux: l.source_taux, motif: l.motif, taux_modifiable: l.taux_modifiable && v.modifiable,
    besoin_net: l.besoin_net, surplus: l.surplus, quantite: l.quantite_arrondie, arrondi: l.arrondi,
    conditionnement: l.conditionnement ? `${l.conditionnement.libelle} (${nombre(l.conditionnement.quantite, 4)} ${l.unit})` : null, nombre: l.nombre_conditionnements,
    detail: l.detail, note: <>Au devis : <strong className="chiffre">{q(l.quantite_devis)} {l.unit}</strong>{l.ecart_devis !== null ? ` dont surplus et arrondi ${q(l.ecart_devis)} ${l.unit}` : ''}{!l.a_jour ? ' — à recalculer' : ''}</> }));
  const nbSurplus = v.lignes.filter((l) => l.statut === 'surplus').length;
  const nbADecider = v.lignes.filter((l) => l.statut === 'a_decider').length;
  return (
    <Panneau titre="Surplus de matériel" className="surplus-panneau">
      <div className="pile">
        <label className="surplus-case">
          <input type="checkbox" checked={v.integre} disabled={!v.modifiable || envoi} onChange={(e) => { void envoyer({ integrer: e.target.checked }).catch(() => undefined); }} />
          <span><strong>Intégrer le surplus au devis</strong><br />
            <span className="petit secondaire">{v.integre
              ? 'Cochée : la quantité vendue des lignes concernées comprend le surplus puis l’arrondi au conditionnement ; le besoin net reste affiché.'
              : 'Décochée : le devis porte le besoin net ; le surplus reste à la charge de l’entreprise (il sera proposé au bon de commande).'}</span></span>
        </label>
        {v.fige && <Alerte titre="Devis émis : figé">La case ne modifie plus ce devis. Pour la commande, le surplus se règle dans le bon de commande du chantier (onglet Matériel).</Alerte>}
        {!v.fige && !v.modifiable && <Alerte>Révision en validation : repassez-la en brouillon pour changer le surplus.</Alerte>}
        {v.integre && !v.a_jour && v.modifiable && <Alerte ton="ambre" titre="Quantités à recalculer" action={<Bouton compact chargement={envoi} onClick={() => { void envoyer({ integrer: true }).catch(() => undefined); }}>Recalculer</Bouton>}>
          Une ligne a été ajoutée ou un article a changé depuis l’intégration.</Alerte>}
        <p className="petit secondaire">{`${nbSurplus} ligne${nbSurplus > 1 ? 's' : ''} avec surplus proposé (taux recommandé 15 à 20 %). `}
          {nbADecider ? `${nbADecider} article${nbADecider > 1 ? 's' : ''} sans famille : surplus à décider dans la bibliothèque de prix (aucun taux n’est deviné). ` : ''}
          Jamais de surplus sur les machines, boîtiers, réfnets, commandes, pompes, la main-d’œuvre ni le fluide (arrondi à la bouteille seulement).</p>
        <EncadreSurplus lignes={vues} libelleQuantite="Après arrondi" regler={v.modifiable ? setRegle : undefined} />
      </div>
      <DialogueTauxSurplus ligne={regle} fermer={() => setRegle(null)} enregistrer={(taux, motif) => envoyer({ integrer: v.integre, taux: [{ ligneId: regle!.cle, taux, motif }] })} />
    </Panneau>
  );
}
